// src/client/renderer/shaders/stone-skin.glsl.ts

export const VERTEX = /* glsl */ `
  #include <common>
  #include <skinning_pars_vertex>

  uniform float uNoiseScale;
  uniform float uDisplacement;
  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  varying float vSurfaceNoise;

  float hash31(vec3 p) {
    return fract(sin(dot(p, vec3(17.13, 73.21, 41.77))) * 43758.5453);
  }

  float valueNoise3(vec3 p) {
    vec3 i = floor(p);
    vec3 f = fract(p);
    vec3 u = f * f * (3.0 - 2.0 * f);

    float n000 = hash31(i + vec3(0.0, 0.0, 0.0));
    float n100 = hash31(i + vec3(1.0, 0.0, 0.0));
    float n010 = hash31(i + vec3(0.0, 1.0, 0.0));
    float n110 = hash31(i + vec3(1.0, 1.0, 0.0));
    float n001 = hash31(i + vec3(0.0, 0.0, 1.0));
    float n101 = hash31(i + vec3(1.0, 0.0, 1.0));
    float n011 = hash31(i + vec3(0.0, 1.0, 1.0));
    float n111 = hash31(i + vec3(1.0, 1.0, 1.0));

    float nx00 = mix(n000, n100, u.x);
    float nx10 = mix(n010, n110, u.x);
    float nx01 = mix(n001, n101, u.x);
    float nx11 = mix(n011, n111, u.x);
    float nxy0 = mix(nx00, nx10, u.y);
    float nxy1 = mix(nx01, nx11, u.y);
    return mix(nxy0, nxy1, u.z);
  }

  void main() {
    vUv = uv;
    #include <skinbase_vertex>
    #include <beginnormal_vertex>
    #include <skinnormal_vertex>
    #include <begin_vertex>
    #include <skinning_vertex>

    vSurfaceNoise = valueNoise3(transformed * uNoiseScale);
    vec3 displaced = transformed + normalize(objectNormal) * ((vSurfaceNoise - 0.5) * uDisplacement);
    transformed = displaced;
    vec4 worldPos = modelMatrix * vec4(transformed, 1.0);
    vWorldPosition = worldPos.xyz;
    vWorldNormal = normalize(mat3(modelMatrix) * objectNormal);
    #include <project_vertex>
  }
`;

export const FRAGMENT = /* glsl */ `
  uniform vec3 uBaseColor;
  uniform vec3 uDarkColor;
  uniform vec3 uCrackColor;
  uniform float uNoiseScale;
  uniform float uCrackScale;
  uniform float uOpacity;
  varying vec2 vUv;
  varying vec3 vWorldNormal;
  varying vec3 vWorldPosition;
  varying float vSurfaceNoise;

  float hash21(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453);
  }

  float valueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = hash21(i + vec2(0.0, 0.0));
    float b = hash21(i + vec2(1.0, 0.0));
    float c = hash21(i + vec2(0.0, 1.0));
    float d = hash21(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }

  void main() {
    vec2 stoneUv = vUv * uNoiseScale;
    float grain = valueNoise(stoneUv) * 0.55 + valueNoise(stoneUv * 2.35 + 11.7) * 0.45;
    float veinNoise = valueNoise(vUv * uCrackScale + vec2(vSurfaceNoise * 2.0, 0.0));
    float crackMask = 1.0 - smoothstep(0.0, 0.035, abs(veinNoise - 0.5));

    vec3 stone = mix(uDarkColor, uBaseColor, grain);
    stone = mix(stone, uCrackColor, crackMask);

    vec3 lightDir = normalize(vec3(0.35, 0.85, 0.45));
    float diffuse = clamp(dot(normalize(vWorldNormal), lightDir), 0.18, 1.0);
    vec3 viewDir = normalize(cameraPosition - vWorldPosition);
    float rim = pow(1.0 - max(dot(normalize(vWorldNormal), viewDir), 0.0), 2.8);

    vec3 color = stone * (0.62 + diffuse * 0.42) + vec3(0.08, 0.075, 0.065) * rim;
    gl_FragColor = vec4(color, uOpacity);
  }
`;
